<template>
	<div>
		<v-autocomplete
			outlined
			single-line
			ref="select"
			height="280"
			item-value="id"
			item-text="title"
			@blur="isFocused = false"
			@focus="isFocused = true"
			:items="profileItemsList"
			class="profile-dropdown-ac"
			:loading="isTestingProfile"
			v-model="selectedProfileId"
			:disabled="isTestingProfile"
			append-icon="mdi-chevron-down"
			label="Choose connection option"
			:menu-props="autocompleteMenuProps"
			:class="{ 'focused-background': isFocused }"
		>
			<template v-slot:prepend-item>
				<div class="width-100 flex-row display-flex pl-2 pr-3 explanation-header flex-justify-space-between">
					<div class="flex-column display-flex flex-align-start">
						<div class="title-text">Connection options</div>
						<div class="desctiption-text">Only affects your feature when it’s shared</div>
					</div>
					<!-- <a class="display-flex flex-row link" :href="connectionTutorialURL" target="_blank">
						<SIcon name="play-connection-tutorial" width="17" class="mr-1" style="margin-top: 3px" />
						<div class="desctiption-text">Watch tutorial</div>
					</a> -->
				</div>
			</template>

			<template v-slot:item="data">
				<div class="width-100">
					<div class="width-100 display-flex flex-row flex-align-center item-container">
						<SIcon :name="data.item.iconName" :width="data.item.iconWidth" class="ml-n2 mr-3" />
						<div class="display-flex flex-column flex-justify-start flex-align-start">
							<div class="title-text">
								{{ data.item.title }}
							</div>
							<div class="desctiption-text">
								{{ data.item.description }}
							</div>
							<div class="desctiption-text mt-n1">
								{{ data.item.additionalDescription }}
							</div>
						</div>
					</div>
					<v-divider v-if="data.item.addDivider" />
				</div>
			</template>
		</v-autocomplete>
		<ProfileConfigurationSection
			:serviceId="serviceId"
			:contextMode="contextMode"
			:widgetFieldMode="widgetFieldMode"
			:narrowLayout="narrowLayout"
			v-if="showConfigurationSection"
			:credentialsData="credentialsData"
			:credentialsType="credentialsType"
			:serviceVersionId="serviceVersionId"
			:overrideSelectProfile="overrideSelectProfile"
			:configurationFieldProps="configurationFieldProps"
		/>
	</div>
</template>
<script>
import api from '@/api';
import { mapGetters } from 'vuex';
import CredentialsService from '@/utils/credentials-service';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import ProfileConfigurationSection from './ProfileConfigurationSection.vue';
import {
	VIRTUAL_PROFILES_IDS,
	PER_USER_CONNECTION_ID,
	EMBEDDED_CONNECTION_ID,
	PER_ACCOUNT_CONNECTION_ID,
} from '@/utils/credentials-service.const';
import { CredentialsTypes, UserCredentialsStatus } from '@ply-io/ply-front-shared-utilities';
export default {
	name: 'ProfileConnectionOptions',
	components: {
		ProfileConfigurationSection,
	},
	props: {
		profileId: { type: Number },
		serviceId: { type: Number },
		contextMode: { type: Boolean },
		widgetFieldMode: { type: Boolean },
		narrowLayout: { type: Boolean },
		credentialsData: { type: Object },
		serviceVersionId: { type: String },
		customFieldProps: { type: Object },
		overrideSelectProfile: { type: Function },
		configurationFieldProps: { type: Object },
		readonly: { type: Boolean, default: false },
		hideExplanation: { type: Boolean, default: false },
		disableRealProfiles: { type: Boolean, default: false },
		includeVirtualProfiles: { type: Boolean, default: false },
		nativeIntegrationAccount: { type: Boolean, default: false },
		credentialsType: { type: String, default: CredentialsTypes.Embedded },
	},
	computed: {
		...mapGetters([isConfigurationsOn.fullPath]),
		selectedProfile() {
			let lookedUpId = this.selectedProfileId;
			if (CredentialsService.isConnectionFromConfiguration(this.credentialsType)) {
				if (this.credentialsType === CredentialsTypes.ProfileBased) {
					lookedUpId = PER_USER_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.AccountBased) {
					lookedUpId = PER_ACCOUNT_CONNECTION_ID;
				}
			}
			return this.profileItemsList.find((profile) => profile.id === lookedUpId) || {};
		},
		autocompleteMenuProps() {
			return { contentClass: 'profile-dropdown', rounded: 'lg' };
		},
		showConfigurationSection() {
			return (
				this[isConfigurationsOn.fullPath] &&
				this.includeVirtualProfiles &&
				CredentialsService.isConnectionFromConfiguration(this.credentialsType)
			);
		},
		selectedProfileId: {
			get() {
				if (this.credentialsType === CredentialsTypes.Embedded) {
					return EMBEDDED_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.ProfileBased) {
					return PER_USER_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.AccountBased) {
					return PER_ACCOUNT_CONNECTION_ID;
				}
				// we shouldn't be here, which credentialsType is defined?
				console.warn('credentials type of node is unknown');
				return null;
			},
			async set(value) {
				await this.connectProfile(value);
			},
		},
	},
	methods: {
		async connectProfile(value, forceSet) {
			if ((value && value !== this.profileId) || forceSet) {
				let profileToSet;
				let validToSet = VIRTUAL_PROFILES_IDS.includes(value);
				let r = {};
				if (!validToSet) {
					// checking profile
					r = await this.checkProfile(value);
					validToSet = r.success;
				}
				// profile is valid
				if (validToSet) {
					this.resetErrorData();
					profileToSet = value;
					this.$emit('profile-set-successful');
				} else {
					this.$emit('profile-set-failed');
					this.profileErr = r.errorJSON;
					this.showDetailsDialog = true;
					this.reconnectingCredsId = value;
					profileToSet = null; // Reset.
				}
				this.$emit('select-profile', profileToSet || value);
			}
		},
		async checkProfile(profileId) {
			if (this.selectedProfile?.status === UserCredentialsStatus.Staled) {
				return {
					success: false,
					errorJSON: this.selectedProfile.error_message,
				};
			} else {
				this.checkingProfileId = profileId;
				this.isTestingProfile = true;
				let ret;
				try {
					ret = await api.testConnection(profileId);
					return ret;
				} catch (error) {
					ret = {
						success: false,
						errorJSON: 'Error occured while testing credentials',
					};
				} finally {
					this.isTestingProfile = false;
					this.checkingProfileId = null;
				}
				return ret;
			}
		},
		resetErrorData() {
			this.reconnectingCredsId = null;
			this.profileErr = null;
		},
	},
	data() {
		return {
			reconnectingCredsId: null,
			isFocused: false,
			profileErr: null,
			searchProfileString: null,
			isTestingProfile: false,
			checkingProfileId: null,
			showDetailsDialog: false,
			connectionTutorialURL: 'https://app.ply.io/', // TODO: Change it to real one when tutorial video is ready
			profileItemsList: [
				{
					iconWidth: '22',
					addDivider: true,
					title: 'One for all',
					id: EMBEDDED_CONNECTION_ID,
					iconName: 'regular-connection-explanation',
					description: 'One central connection used for all users',
				},
				{
					iconWidth: '22',
					iconName: 'general-connection-explanation',
					title: 'Regular connection, defined in general setup',
					description: 'One central connection used for all users. Editors can connect an ',
					additionalDescription: 'account without editing the feature. Useful for installables.',
					...CredentialsService.getPerAccountProfileItem(),
				},
				{
					iconWidth: '28',
					iconName: 'personal-connection-explanation',
					title: 'Per user connection, defined in personal setup',
					additionalDescription: 'must be completed before using the feature.',
					description: 'Each user will connect their own account for this step. Personal setup',
					...CredentialsService.getPerUserProfileItem(),
				},
			],
		};
	},
	watch: {
		showDetailsDialog: {
			handler(newV) {
				if (!newV) {
					// when closing details dialog, reset the related error data
					this.resetErrorData();
				}
			},
		},
		isTestingProfile(newV) {
			this.$emit('set-testing-profile', newV);
		},
	},
};
</script>
<style scoped lang="scss">
.profile-dropdown-ac {
	::v-deep {
		.v-label {
			font-size: 12px;
			margin-top: -10px;
		}
		.v-input__icon {
			margin-top: -10px;
		}
	}
}

.explanation-header {
	height: 44px;
	box-shadow: 0px 1px 2px #747f8d55;
}

.title-text {
	font-size: 12px;
	font-weight: 600;
	color: var(--primary-font-color);
}

.desctiption-text {
	opacity: 0.4;
	font-size: 12px;
	font-weight: normal;
	color: var(--primary-font-color);
}
.link {
	cursor: pointer;
	text-decoration: underline;
	color: var(--primary-font-color);
}

.item-container {
	height: 60px;
	&.border {
		border-bottom: 1px solid var(--border-color);
	}
}
</style>
